import {
  BankAccountProps,
  BankAccountStatus,
  BankAccountProvider,
} from "./bank-account.types";

/**
 * BankAccount entity.
 * Encapsulates bank account data and business rules.
 *
 * Pure TypeScript - no React imports.
 */
export class BankAccount {
  private readonly props: BankAccountProps;

  private constructor(props: BankAccountProps) {
    this.props = props;
  }

  /**
   * Creates a BankAccount entity with validation.
   * @throws Error if required fields are missing or invalid
   */
  static create(props: BankAccountProps): BankAccount {
    if (props.id === undefined || props.id === null) {
      throw new Error("Bank account id is required");
    }

    if (!props.accountNumber) {
      throw new Error("Account number is required");
    }

    if (!props.bankName) {
      throw new Error("Bank name is required");
    }

    return new BankAccount(props);
  }

  // ==================== Getters ====================

  get id(): number {
    return this.props.id;
  }

  get userId(): number {
    return this.props.userId;
  }

  get countryName(): string {
    return this.props.countryName;
  }

  get countryCode(): string {
    return this.props.countryCode;
  }

  get currency(): string {
    return this.props.currency;
  }

  get bankName(): string {
    return this.props.bankName;
  }

  get bankCode(): string {
    return this.props.bankCode;
  }

  get accountNumber(): string {
    return this.props.accountNumber;
  }

  get accountName(): string {
    return this.props.accountName;
  }

  get statusId(): string {
    return this.props.statusId;
  }

  get status(): BankAccountStatus {
    return this.props.status;
  }

  get branchCode(): string | null {
    return this.props.branchCode;
  }

  get provider(): BankAccountProvider {
    return this.props.provider;
  }

  get createdAt(): string {
    return this.props.createdAt;
  }

  get updatedAt(): string {
    return this.props.updatedAt;
  }

  get loginUrl(): string | null {
    return this.props.loginUrl ?? null;
  }

  // ==================== Business Methods ====================

  /**
   * Check if the bank account is active.
   */
  isActive(): boolean {
    return this.props.status === "Active";
  }

  /**
   * Check if the bank account is pending activation.
   */
  isPending(): boolean {
    return this.props.status === "Pending";
  }

  /**
   * Check if this is a Stripe account (EU users).
   */
  isStripeAccount(): boolean {
    return this.props.provider === "stripe";
  }

  /**
   * Whether the provider is Stripe (case-insensitive).
   */
  get isStripe(): boolean {
    return this.props.provider.toLowerCase() === "stripe";
  }

  /**
   * Whether this is a Stripe account with Active status.
   */
  get isStripeAndActive(): boolean {
    return this.isStripe && this.props.status.toLowerCase() === "active";
  }

  /**
   * Whether this is a Stripe account with Inactive status.
   */
  get isStripeAndInactive(): boolean {
    return this.isStripe && this.props.status.toLowerCase() === "inactive";
  }

  /**
   * Check if this is a local payment provider (Paystack/Flutterwave).
   */
  isLocalProvider(): boolean {
    return this.props.provider === "paystack" || this.props.provider === "flutterwave";
  }

  /**
   * Returns the raw properties for serialization or mapping.
   */
  toProps(): BankAccountProps {
    return {...this.props};
  }
}
